import { useState, useRef, useEffect, useCallback } from 'react'
import type { Tables } from '@/types/supabase'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { useMeeting } from '../../contexts/MeetingContext'
import { useRealtimeReuniao } from '../../hooks/useRealtimeReuniao'
import useAudioRecorder from '../../hooks/useAudioRecorder'
import ReuniaoAoVivoDialogs from './ReuniaoAoVivoDialogs'
import AoVivoHeader, { PauseOverlay } from './AoVivoHeader'
import AoVivoSidebar from './AoVivoSidebar'
import AoVivoMobileToolbar from './AoVivoMobileToolbar'
import AoVivoNotasPanel from './AoVivoNotasPanel'
import CameraInline from '../captura/CameraInline'
import CapturaDrawCanvas from '../captura/CapturaDrawCanvas'
import { deviceLabel } from '../../lib/deviceId'

type Reuniao = Tables<'reunioes'> & {
  reuniao_participantes?: Tables<'reuniao_participantes'>[]
}

interface LocalImage {
  tempId: number
  dbId?: number
  localUrl?: string
  uploading: boolean
  remotePath?: string
}

const BUCKET = 'reunioes'
/** Teto de duração gravada. Acima disso é aparelho esquecido aberto, não reunião. */
const MAX_DURACAO_SEGUNDOS = 8 * 60 * 60

function countdownMin(reuniao: Reuniao): number | null {
  if (!reuniao.hora_fim || !reuniao.data_reuniao) return null
  const end = new Date(`${reuniao.data_reuniao}T${reuniao.hora_fim}:00`).getTime()
  return Math.round((end - Date.now()) / 60000)
}

export default function ReuniaoAoVivo() {
  const { meeting, seconds, paused, updateMeeting, endMeeting, cancelMeeting, toCapsule, toCompact, pauseMeeting, resumeMeeting } = useMeeting()
  const [showCancelDlg,   setShowCancelDlg]   = useState(false)
  const [showEncerrarDlg, setShowEncerrarDlg] = useState(false)
  const [mobileSidebar,   setMobileSidebar]   = useState(false)
  const [showCamera,      setShowCamera]      = useState(false)
  const [showDraw,        setShowDraw]        = useState(false)
  const reuniao   = meeting?.reuniao as Reuniao | undefined
  const isMeet    = !!reuniao?.link_virtual

  // ── Realtime multi-device ──
  const rt = useRealtimeReuniao(reuniao?.id)
  const notasOwnerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [notas, setNotas]   = useState(reuniao?.notas_reuniao || '')
  const notasTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleNotas(val: string) {
    setNotas(val)
    rt.claimNotasOwnership()
    clearTimeout(notasOwnerTimer.current)
    notasOwnerTimer.current = setTimeout(() => rt.releaseNotasOwnership(), 5000)
    clearTimeout(notasTimer.current)
    notasTimer.current = setTimeout(() => updateMeeting({ notas_reuniao: val }).catch(() => {}), 3000)
  }

  const parts = reuniao?.reuniao_participantes || []
  // Default: todos os participantes adicionados ficam marcados como presentes.
  // Usuário desmarca quem não compareceu durante a reunião (e pode editar depois pelo modal).
  const initialPresentes = (reuniao?.presentes_nomes && reuniao.presentes_nomes.length > 0)
    ? reuniao.presentes_nomes
    : parts.map(p => p.nome)
  const [presentes, setPresentes] = useState(new Set<string>(initialPresentes))

  // ── Gravação em outro aparelho ──
  // Abrir a reunião num segundo aparelho (celular, para fotografar um croqui)
  // fazia esse aparelho começar a gravar sozinho, em paralelo. Agora só grava
  // aqui se ninguém mais estiver gravando, ou se for escolha explícita.
  const [gravarMesmoAssim, setGravarMesmoAssim] = useState(false)
  const [bloqueadoPorOutro, setBloqueadoPorOutro] = useState(false)
  const outroGravando = rt.gravandoEmOutroDevice

  // O bloqueio é PEGAJOSO. Se dependesse só de `outroGravando`, este aparelho
  // começaria a gravar sozinho no instante em que o outro parasse — encerrar no
  // notebook faria o celular, esquecido aberto no bolso, iniciar uma gravação
  // nova. Uma vez bloqueado, só sai por decisão explícita.
  // Guarda o rótulo de onde a gravação está: `outroGravando` some quando o outro
  // aparelho para, mas o bloqueio continua, e o texto do aviso precisa sobreviver.
  const [outroLabel, setOutroLabel] = useState<string | null>(null)
  useEffect(() => {
    if (outroGravando && !gravarMesmoAssim) {
      setBloqueadoPorOutro(true)
      setOutroLabel(deviceLabel(outroGravando.stableId))
    }
  }, [outroGravando, gravarMesmoAssim])

  const gravacaoBloqueada = bloqueadoPorOutro && !gravarMesmoAssim
  const gravacaoDeApoio = gravarMesmoAssim && bloqueadoPorOutro

  // ── Audio recorder hook ──
  const recorder = useAudioRecorder({
    reuniaoId: reuniao?.id,
    updateMeeting,
    broadcastRecState: rt.broadcastRecState,
    disabled: isMeet || !rt.presenceReady || gravacaoBloqueada,
    secondary: gravacaoDeApoio,
    onRecordingChange: rt.setRecordingPresence,
  })

  const [imagens, setImagens] = useState<LocalImage[]>([])

  const reuniaoId = reuniao?.id
  useEffect(() => {
    if (!reuniaoId) return
    setNotas(reuniao?.notas_reuniao || '')
    const nextPresentes = (reuniao?.presentes_nomes && reuniao.presentes_nomes.length > 0)
      ? reuniao.presentes_nomes
      : (reuniao?.reuniao_participantes ?? []).map(p => p.nome)
    setPresentes(new Set<string>(nextPresentes))
    setImagens([])
  }, [reuniaoId]) // eslint-disable-line react-hooks/exhaustive-deps

  // On unmount: flush pending debounced saves instead of discarding them
  const notasRef = useRef<string>(notas)
  useEffect(() => { notasRef.current = notas }, [notas])
  const presentesRef = useRef<Set<string>>(presentes)
  useEffect(() => { presentesRef.current = presentes }, [presentes])

  useEffect(() => () => {
    // Flush pending notas save
    if (notasTimer.current) {
      clearTimeout(notasTimer.current)
      updateMeeting({ notas_reuniao: notasRef.current }).catch(() => {})
    }
    // Flush pending presentes save
    if (presentesTimer.current) {
      clearTimeout(presentesTimer.current)
      updateMeeting({ presentes_nomes: [...presentesRef.current] }).catch(() => {})
    }
    clearTimeout(notasOwnerTimer.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime: sync notas de outro device ──
  useEffect(() => {
    if (rt.remoteNotas === null) return
    if (!rt.isNotasOwner) setNotas(rt.remoteNotas)
  }, [rt.remoteNotas]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime: sync presentes de outro device ──
  useEffect(() => {
    if (!rt.remotePresentes) return
    setPresentes(prev => new Set([...prev, ...rt.remotePresentes]))
  }, [rt.remotePresentes])

  // ── Realtime: nova imagem de outro device ──
  useEffect(() => {
    if (!rt.remoteImagens.length) return
    const last = rt.remoteImagens[rt.remoteImagens.length - 1]
    setImagens(prev => {
      if (prev.some(i => i.dbId === last.id)) return prev
      return [...prev, { tempId: last.id, dbId: last.id, uploading: false, remotePath: last.storage_path }]
    })
  }, [rt.remoteImagens.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime: reunião encerrada em outro device ──
  useEffect(() => {
    if (rt.remoteStatus === 'REALIZADA') {
      toast.info('Reunião encerrada em outro dispositivo')
      endMeeting()
    }
  }, [rt.remoteStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Atalhos: Ctrl+P pausa · Ctrl+E encerrar
  const shortcutRef = useRef<{ paused?: boolean }>({})
  useEffect(() => { shortcutRef.current = { paused } })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!e.ctrlKey) return
      const k = e.key.toLowerCase()
      if (k === 'p') { e.preventDefault(); shortcutRef.current.paused ? handleResume() : handlePause() }
      if (k === 'e') { e.preventDefault(); setShowEncerrarDlg(true) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const presentesTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function togglePresente(nome: string) {
    setPresentes(prev => {
      const n = new Set(prev); n.has(nome) ? n.delete(nome) : n.add(nome)
      clearTimeout(presentesTimer.current)
      presentesTimer.current = setTimeout(() => updateMeeting({ presentes_nomes: [...n] }).catch(() => {}), 3000)
      return n
    })
  }

  async function uploadImage(blob: Blob) {
    const ext = blob.type.split('/')[1] || 'png'
    const path = `${reuniao!.id}/img_${Date.now()}.${ext}`
    const tempId = Date.now() + Math.random()
    setImagens(prev => [...prev, { tempId, localUrl: URL.createObjectURL(blob), uploading: true }])
    try {
      const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { upsert: true })
      if (error) throw error
      await supabase.from('reuniao_imagens').insert({ reuniao_id: reuniao!.id, storage_path: path })
      setImagens(prev => prev.map(i => i.tempId === tempId ? { ...i, uploading: false } : i))
    } catch { toast.error('Erro ao salvar imagem'); setImagens(prev => prev.filter((i: LocalImage) => i.tempId !== tempId)) }
  }

  function handlePause() {
    if (!isMeet) recorder.pauseRecording()
    pauseMeeting()
  }

  function handleResume() {
    if (!isMeet) recorder.resumeRecording()
    resumeMeeting()
  }

  async function encerrar() {
    if (recorder.recState === 'uploading') {
      toast.warning('Aguarde o envio da gravação terminar')
      return
    }
    if (recorder.isRec) await recorder.waitForUpload()
    clearTimeout(notasTimer.current); clearTimeout(presentesTimer.current)

    // Aparelho de apoio sai da sessão sem encerrar a reunião dos outros. Sem isto,
    // fechar no celular encerraria a reunião e dispararia a transcrição enquanto o
    // notebook ainda está gravando — a transcrição sairia pela metade.
    if (gravacaoDeApoio) {
      try {
        await updateMeeting({ notas_reuniao: notas, presentes_nomes: [...presentes] })
      } catch { /* as notas já sobem sozinhas pelo autosave */ }
      endMeeting()
      toast.success('Você saiu da sessão. A gravação principal continua no outro aparelho.')
      return
    }

    try {
      // A reunião pode já ter sido encerrada em outro aparelho. Foi o que produziu
      // `duracao_segundos = 34.320` (9h32 contra 3h11 reais) na REU04 do Moena: o
      // celular ficou aberto e, ao ser encerrado horas depois, reescreveu a duração
      // com o relógio de parede. Encerramento tardio não reescreve status nem duração.
      const { data: atual } = await supabase.from('reunioes')
        .select('status').eq('id', reuniao!.id).single()
      const jaEncerrada = atual?.status === 'REALIZADA'

      const iniciado_em = reuniao!.iniciado_em || new Date(Date.now() - seconds * 1000).toISOString()
      const wallClock = reuniao!.iniciado_em
        ? Math.round((Date.now() - new Date(reuniao!.iniciado_em!).getTime()) / 1000)
        : seconds
      // Teto de 8h: acima disso é aparelho esquecido aberto, não reunião.
      const realDuration = Math.min(wallClock, MAX_DURACAO_SEGUNDOS)
      // NÃO sobrescrever data_reuniao / hora_reuniao / hora_fim — são os valores agendados.
      // O início real está em iniciado_em, a duração real em duracao_segundos.
      await updateMeeting(jaEncerrada
        ? { notas_reuniao: notas, presentes_nomes: [...presentes] }
        : {
            status: 'REALIZADA', notas_reuniao: notas, duracao_segundos: realDuration,
            presentes_nomes: [...presentes], iniciado_em,
          })
      if (parts.length) {
        await Promise.all(parts.map(p =>
          supabase.from('reuniao_participantes')
            .update({ presente: presentes.has(p.nome) })
            .eq('id', p.id)
        ))
      }
      // Enqueue async processing (transcription → ata → RAG → cleanup)
      const hasSegments = recorder.segmentosRef.current.length > 0 || reuniao!.storage_audio
      if (hasSegments) {
        try {
          const { error: fnError } = await supabase.functions.invoke('process-reuniao', {
            body: { reuniao_id: reuniao!.id }
          })
          if (fnError) throw fnError
          toast.success('Reunião encerrada — transcrição e ata serão geradas automaticamente')
        } catch (e) {
          console.error('Processamento em background falhou:', e)
          toast.warning('Reunião encerrada, mas o processamento automático falhou. Use o botão "Transcrever" no detalhe da reunião.', { duration: 8000 })
        }
      } else if (isMeet && reuniao!.calendar_event_id) {
        toast.success('Reunião encerrada')
        supabase.functions.invoke('importar-meet', {
          body: { reuniao_id: reuniao!.id }
        }).then(({ data }) => {
          if (data?.ok && !data?.skipped) {
            toast.success('Transcrição do Meet importada automaticamente')
          } else {
            toast.info('O Google Meet pode levar de 5 a 30 min para gerar a transcrição. Use o botão "Importar do Meet" quando estiver pronta.', { duration: 8000 })
          }
        }).catch(() => {
          toast.info('A transcrição do Meet será importada posteriormente. Use o botão "Importar do Meet".', { duration: 6000 })
        })
      } else {
        toast.success('Reunião encerrada')
      }
      endMeeting()
      window.dispatchEvent(new CustomEvent('reuniao-encerrada'))
    } catch (err: unknown) {
      console.error('Erro ao encerrar reunião:', err)
      toast.error(`Erro ao encerrar reunião: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  if (!reuniao) return null

  const cd = countdownMin(reuniao)
  const cdColor = cd === null ? null : cd < 0 ? 'var(--color-vermelho)' : cd < 15 ? 'var(--color-dourado)' : '#555'
  const cdLabel = cd === null ? null : cd < 0 ? `+${Math.abs(cd)}min` : `→${cd}min`

  return (
    <>
    <ReuniaoAoVivoDialogs
      showEncerrarDlg={showEncerrarDlg} setShowEncerrarDlg={setShowEncerrarDlg}
      showCancelDlg={showCancelDlg} setShowCancelDlg={setShowCancelDlg}
      isRec={recorder.isRec} isMeet={isMeet} onEncerrar={encerrar}
      onConfirmCancel={async () => {
        if (recorder.isRec) recorder.cancelRecording()
        await cancelMeeting()
        toast.success('Sessão descartada')
      }}
    />

    <div className="fixed inset-0 z-[300] flex flex-col bg-bg">
      <AoVivoHeader
        reuniao={reuniao} seconds={seconds} paused={paused}
        cdLabel={cdLabel} cdColor={cdColor} recState={recorder.recState} recSecs={recorder.recSecs}
        isRec={recorder.isRec} lastSaved={recorder.lastSaved} pendingCount={recorder.pendingCount}
        onPause={handlePause} onResume={handleResume} onEncerrar={encerrar}
        onCancel={() => setShowCancelDlg(true)} onToCapsule={toCapsule} onToCompact={toCompact}
        mobileSidebar={mobileSidebar} onToggleSidebar={() => setMobileSidebar(v => !v)}
        deviceCount={rt.deviceCount} outroGravando={!!outroGravando}
        isMeet={isMeet}
      />

      <div className="flex-1 overflow-hidden flex flex-col md:flex-row relative">
        {paused && <PauseOverlay recState={recorder.recState} onResume={handleResume} isMeet={isMeet} />}

        <div className={`${mobileSidebar ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-[200px] shrink-0 border-b md:border-b-0 md:border-r border-white/5`}>
          <AoVivoSidebar
            parts={parts} presentes={presentes} togglePresente={togglePresente}
            recState={recorder.recState} recSecs={recorder.recSecs} pendingCount={recorder.pendingCount}
            transcribedCount={recorder.transcribedCount} isMeet={isMeet}
          />
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Só aparece para quem está bloqueado ou gravando de apoio. Quem está
              gravando normalmente não recebe convite para gravar de novo. */}
          {!isMeet && (gravacaoBloqueada || gravacaoDeApoio) && (
            <div role="status" aria-live="polite"
              className="flex items-start gap-2.5 px-4 py-3 border-b border-white/5 bg-white/[0.03]">
              <span className="material-symbols-outlined text-[16px] text-[#999] mt-px shrink-0">
                {gravacaoDeApoio ? 'graphic_eq' : 'mic_off'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-[#ccc]">
                  {gravacaoDeApoio
                    ? 'Gravando também neste aparelho'
                    : outroGravando
                      ? `A gravação desta reunião está rodando no ${outroLabel || 'outro aparelho'}`
                      : `A gravação desta reunião ficou no ${outroLabel || 'outro aparelho'}`}
                </p>
                <p className="text-[10px] text-[#888] leading-relaxed mt-0.5">
                  {gravacaoDeApoio
                    ? 'O áudio daqui fica guardado junto com o da gravação principal, mas a transcrição usa só a principal. Duas gravações do mesmo encontro embaralham o texto.'
                    : 'Este aparelho não vai gravar, para não duplicar o áudio da reunião. Suas anotações e fotos continuam funcionando normalmente.'}
                </p>
              </div>
              {!gravacaoDeApoio && (
                <button
                  onClick={() => setGravarMesmoAssim(true)}
                  className="shrink-0 text-[10px] font-semibold px-3.5 py-2.5 min-h-[40px] rounded-full bg-white/[0.06] border border-white/15 text-[#ccc] hover:bg-white/10 transition-colors cursor-pointer">
                  Gravar aqui também
                </button>
              )}
            </div>
          )}

          <AoVivoNotasPanel
            notas={notas}
            onNotasChange={handleNotas}
            isNotasOwner={rt.isNotasOwner}
            imagens={imagens}
            onUploadImage={uploadImage}
          />

          <AoVivoMobileToolbar
            onPhoto={() => setShowCamera(true)}
            onDraw={() => setShowDraw(true)}
          />
        </div>
      </div>
    </div>

    {showCamera && (
      <CameraInline
        onCapture={blob => { uploadImage(blob) }}
        onClose={() => setShowCamera(false)}
      />
    )}

    {showDraw && (
      <CapturaDrawCanvas
        onConfirm={blob => { uploadImage(blob); setShowDraw(false) }}
        onCancel={() => setShowDraw(false)}
      />
    )}
    </>
  )
}
